import { t } from '@lingui/core/macro';
import dayjs from 'dayjs';
import { type Point, type PointerEventObject } from 'highcharts';
import { useMemo } from 'react';

import { type CompanyType, useTrafficVisitTrendByVendor } from '@/api/common';
import Flare, { Area, Axis, type ChartCallbackFunction, Legend, Tooltip } from '@/components/Flare';
import { Notes } from '@/components/Flare/annotation';
import { usePageFilterContext } from '@/components/page';
import { NotesPanel } from '@/components/panels';
import {
  DateISOFormat,
  MonthDateFormat,
  MonthDateShortFormat,
  MonthDateYearFormat,
} from '@/constants/formats';
import TrendSummaryColumn from '@/features/TrendSummaryColumn';
import { numberFormat } from '@/helper/numberFormatter';

type Props = {
  company?: CompanyType;
  marginLeft: number;
  marginRight: number;
  onHoverChart?: (event: PointerEventObject, point: Point) => void;
  onHoverLeaveChart?: (event: MouseEvent) => void;
  onPostRenderChart?: ChartCallbackFunction;
};

const CompanyDetailTrendChart = ({
  company,
  marginLeft,
  marginRight,
  onHoverChart,
  onHoverLeaveChart,
  onPostRenderChart,
}: Props) => {
  const {
    pageFilters: { startDate, endDate },
  } = usePageFilterContext();

  const {
    visitTrend: data,
    isLoading,
    error,
  } = useTrafficVisitTrendByVendor(startDate, endDate, { companyId: company?.id });

  const chartData = useMemo(() => {
    if (!data || !company) {
      return undefined;
    }

    return data?.map((series) => ({
      id: series.vendor.id,
      name: `${series.vendor.name} - ${series.vendor.channel!.name}`,
      custom: { seriesTitle: series.vendor.name, seriesSubtitle: series.vendor.channel!.name },
      data: series.data,
    }));
  }, [data, company]);

  const xAxisTickPositions = useMemo(() => {
    return data?.[0]?.data.map((d) => +dayjs(d.sundayOfWeek));
  }, [data]);

  const hasAxisTicks = xAxisTickPositions && xAxisTickPositions?.length > 0;
  const chartStartDate = hasAxisTicks ? dayjs(xAxisTickPositions?.[0]) : undefined;
  const chartEndDate = hasAxisTicks
    ? dayjs(xAxisTickPositions?.[xAxisTickPositions.length - 1])
    : undefined;

  return (
    <NotesPanel
      startDate={chartStartDate && chartStartDate.format(DateISOFormat)}
      endDate={chartEndDate && chartEndDate.format(DateISOFormat)}
      title={t`Visit Trends`}
      verifyC99Tag
      noPadding
      style={{ height: 625 }}
    >
      <Flare
        key={company?.id}
        data={chartData}
        colors={data?.map((series) => series.vendor.color!) ?? []}
        description={t`A trend chart showing a running total of visits by channel stacked on top of each other`}
        isLoading={isLoading || !company}
        error={error}
        marginTop={60}
        chartMarginLeft={marginLeft}
        chartMarginRight={marginRight}
        onHoverChart={onHoverChart}
        onHoverLeaveChart={onHoverLeaveChart}
        postRender={onPostRenderChart}
      >
        <Legend />
        <Area x="sundayOfWeek" y="runningTotalVisits" position="stack" />
        <Axis
          position="left"
          labelFormat={(item) => numberFormat(item.value, { compact: true })}
          tickPixelInterval={125}
        />
        <Axis
          type="datetime"
          position="bottom"
          crosshair="line"
          tickPositions={xAxisTickPositions}
          labelFormat={(item) => dayjs(item.value).utc().format(MonthDateShortFormat)}
          min={chartStartDate && +chartStartDate}
          max={chartEndDate && +chartEndDate}
        />
        <Tooltip
          shared
          titleFormat={(point) => {
            const rangeEndDate = dayjs(point?.x).add(6, 'd').utc();
            return t`${chartStartDate?.format(MonthDateFormat) ?? ''} - ${rangeEndDate.format(MonthDateYearFormat)} Visits`;
          }}
          rowValueFormat={(point) => numberFormat(point.y)}
          showTotalRow
        />
        <Notes />
        <TrendSummaryColumn width={marginRight} />
      </Flare>
    </NotesPanel>
  );
};

export default CompanyDetailTrendChart;
